import { isNotDefined, makeValuesPredicate } from '@sgme/fp';
import { format, parseISO } from 'date-fns';
import { useLocalDateFormat } from 'hooks/useLocalDateFormat';
import type { FieldType } from 'models/forms/fields/FieldMetadata';
import { KeyboardEvent, useEffect, useState } from 'react';
export function needLostInput(type: FieldType) {
  switch (type) {
    case 'AmountField':
    case 'PercentField':
    case 'NumberField':
    case 'DateField':
    case 'DatesListField':
    case 'CustomizableTenorField':
    case 'LockablePercentField':
      return true;
    default:
      return false;
  }
}

const digitChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
export const isDigit = makeValuesPredicate(digitChars);

const numberChars = [...digitChars, '.'];
export const isNumber = makeValuesPredicate(numberChars);

const dateChars = [...digitChars, '/'];
export const isDate = makeValuesPredicate(dateChars);

/** Keyboard */
export function isLetter(key: string): boolean {
  if (key.length !== 1) {
    return false;
  }
  const charCode = key.charCodeAt(0);
  return (charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122);
}

export function doesKeyLooksLikeValidInputForComponent(key: string, type: FieldType): boolean {
  switch (type) {
    case 'AmountField':
    case 'PercentField':
    case 'NumberField':
    case 'CustomizableTenorField':
    case 'LockablePercentField':
      return isNumber(key);
    case 'DateField':
    case 'DatesListField':
      return isDate(key);
    case 'UnderlyingsField':
    case 'MultipleUnderlyingsField':
    case 'ClientListField':
      return isNumber(key) || isLetter(key);
    default:
      return false;
  }
}

export function isCtrlKeyPressed(event: KeyboardEvent<Element>): boolean {
  return event.ctrlKey;
}

export function isSpaceBarKeyPressed(event: KeyboardEvent<Element>): boolean {
  return event.key === ' ';
}

export function isDeleteKeyPressed(event: KeyboardEvent<Element>): boolean {
  return event.key === 'Delete';
}

export function areCopyActionKeysPressed(event: KeyboardEvent<Element>): boolean {
  const keys = ['C', 'c', 'X', 'x'];
  return isCtrlKeyPressed(event) && keys.includes(event.key);
}

export function arePasteActionKeysPressed(event: KeyboardEvent<Element>): boolean {
  return isCtrlKeyPressed(event) && ['V', 'v'].includes(event.key);
}

/* End keyboard **/

function formatDateLikeDatePicker(value: string | null | undefined, dateFormat: string) {
  if (isNotDefined(value)) {
    return '';
  }
  const date = parseISO(value);
  return format(date, dateFormat);
}

function getInitialLostInput(columnType: FieldType, value: unknown, dateFormat: string) {
  switch (columnType) {
    case 'DateField':
      return formatDateLikeDatePicker(value as string, dateFormat);
    case 'DatesListField':
      return '';
    default:
      return value;
  }
}

export function useLostInput(
  columnType: FieldType,
  value: unknown,
  lostInputKey: string | undefined,
): string | undefined {
  const [dateFormat] = useLocalDateFormat();
  const initialLostInput = getInitialLostInput(columnType, value, dateFormat);
  const [lostInputValue, setLostInputValue] = useState(initialLostInput);
  // When triggering edit mode by starting to enter a number, we will lose the first digit of input unless we use this hack
  // biome-ignore lint/correctness/useExhaustiveDependencies: I don't dare to change these deps
  useEffect(() => {
    // We only support automatically triggering edit mode for percent cell
    if (lostInputKey !== undefined && needLostInput(columnType)) {
      const newValue = lostInputKey;
      setLostInputValue(newValue);
    }
  }, [value, lostInputKey, columnType]);
  if (needLostInput(columnType)) {
    return lostInputValue as string | undefined;
  }
}
